import { useEffect, useState } from "react";
import { mutate } from "swr";
import { api, WEEKDAYS_SK } from "@/lib/app";
import { X } from "@phosphor-icons/react";
import { toast } from "sonner";
import DataImport from "@/components/DataImport";

const DEFAULT_DAY = (closed = false) => ({
  open_from: closed ? "" : "07:00",
  open_to: closed ? "" : "18:00",
  min_morning: 3,
  min_evening: 4,
  closed,
});

const NOVA_LESNA_PRESET = {
  0: { open_from: "07:00", open_to: "18:00", min_morning: 3, min_evening: 4, closed: false },
  1: { open_from: "07:00", open_to: "18:00", min_morning: 3, min_evening: 4, closed: false },
  2: { open_from: "07:00", open_to: "18:00", min_morning: 3, min_evening: 4, closed: false },
  3: { open_from: "07:00", open_to: "18:00", min_morning: 3, min_evening: 4, closed: false },
  4: { open_from: "07:00", open_to: "18:00", min_morning: 3, min_evening: 3, closed: false },
  5: { open_from: "07:00", open_to: "12:00", min_morning: 6, min_evening: 0, closed: false },
  6: { open_from: "", open_to: "", min_morning: 0, min_evening: 0, closed: true },
};

export default function StoreFormDialog({ onClose, store = null, defaultCompanyId = null }) {
  const isEdit = !!store;
  const [code, setCode] = useState(store?.code || "");
  const [name, setName] = useState(store?.name || "");
  const [perf, setPerf] = useState(store?.required_hourly_performance || 120);
  const [trainingDays, setTrainingDays] = useState(store?.forecast_training_days ?? 0);
  const [tidoCenterId, setTidoCenterId] = useState(store?.tido_center_id ?? "");
  const [companyId, setCompanyId] = useState(store?.company_id ?? defaultCompanyId ?? "");
  const [companies, setCompanies] = useState([]);
  const [days, setDays] = useState(() => {
    if (store?.day_configs && Object.keys(store.day_configs).length) return store.day_configs;
    return NOVA_LESNA_PRESET;
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data || [])).catch(() => setCompanies([]));
  }, []);

  const updateDay = (idx, key, val) => {
    setDays((d) => ({ ...d, [idx]: { ...d[idx], [key]: val } }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!code || !name) {
      toast.error("Vyplňte kód a názov.");
      return;
    }
    setSaving(true);
    try {
      // required_hourly_performance is read-only on the client — it is
      // populated from TiDo on sync. Excluding it from the payload prevents
      // the UI from overwriting a fresh TiDo value with a stale local one.
      const payload = {
        code,
        name,
        forecast_training_days: parseInt(trainingDays) || 0,
        tido_center_id: tidoCenterId === "" || tidoCenterId === null ? null : parseInt(tidoCenterId),
        company_id: companyId || null,
        day_configs: days,
      };
      if (!isEdit) {
        // On create, we still send a default so the store has a starting value
        payload.required_hourly_performance = parseFloat(perf) || 120;
      }
      if (isEdit) {
        await api.put(`/stores/${store.id}`, payload);
        toast.success("Uložené");
        mutate(`/stores/${store.id}`);
      } else {
        await api.post("/stores", payload);
        toast.success("Stredisko vytvorené");
      }
      mutate("/stores");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Chyba pri ukladaní");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full my-8" data-testid="store-form-dialog">
        <div className="flex items-center justify-between p-5 border-b border-[var(--border-default)]">
          <h2 className="font-display text-xl font-semibold">{isEdit ? "Upraviť stredisko" : "Nové stredisko"}</h2>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]" aria-label="Zavrieť" data-testid="close-form-button">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="label-eyebrow block mb-1">Kód</label>
              <input data-testid="store-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="110041" className="w-full" disabled={isEdit} />
            </div>
            <div className="md:col-span-2">
              <label className="label-eyebrow block mb-1">Názov</label>
              <input data-testid="store-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nová Lesná" className="w-full" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label-eyebrow block mb-1">Požadovaný hodinový výkon (€/osobohodina)</label>
              <input
                data-testid="store-perf-input"
                type="number"
                step="1"
                value={perf}
                readOnly
                disabled
                className="w-40 bg-[var(--bg-page)] text-[var(--text-secondary)] cursor-not-allowed"
              />
              <p className="text-[11px] text-[var(--text-muted)] mt-1">
                Preberá sa z TiDo pri synchronizácii — needituje sa tu. Ak chcete zmenu, upravte to v TiDo administrácii daného strediska.
              </p>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Učiace obdobie predikcie</label>
              <select
                data-testid="store-training-days-input"
                value={trainingDays}
                onChange={(e) => setTrainingDays(parseInt(e.target.value))}
                className="w-full max-w-xs"
              >
                <option value={0}>Celá dostupná história (predvolené)</option>
                <option value={30}>Posledných 30 dní</option>
                <option value={60}>Posledných 60 dní</option>
                <option value={90}>Posledných 90 dní (3 mesiace)</option>
                <option value={180}>Posledných 180 dní (6 mesiacov)</option>
                <option value={365}>Posledný 1 rok</option>
                <option value={730}>Posledné 2 roky</option>
              </select>
              <p className="text-[11px] text-[var(--text-muted)] mt-1">
                Z koľkých dní pred dátumom má model čítať históriu. Kratšie = lepšie reaguje na zmeny (sezóna, otvorenie konkurencie); dlhšie = stabilnejšie priemery.
              </p>
            </div>
          </div>

          <div className="card-flat p-4 bg-[var(--brand)]/5 space-y-3">
            <div>
              <label className="label-eyebrow block mb-1">Klient (subdoména)</label>
              <select
                data-testid="store-company-select"
                value={companyId || ""}
                onChange={(e) => setCompanyId(e.target.value)}
                className="w-full max-w-sm"
              >
                <option value="">— Nezaradené —</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.subdomain}.tido.sk)</option>
                ))}
              </select>
              <p className="text-[11px] text-[var(--text-muted)] mt-1">
                Priradenie ku klientovi (COOP Poprad, Karmen…). Klienta pridávaj v sekcii „Klienti“.
              </p>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">TiDo integrácia — center_id (IDP)</label>
              <input
                data-testid="store-tido-center-id-input"
                type="number"
                placeholder="napr. 8"
                value={tidoCenterId ?? ""}
                onChange={(e) => setTidoCenterId(e.target.value)}
                className="w-40"
              />
              <p className="text-[11px] text-[var(--text-muted)] mt-1">
                Ak je vyplnené, umožní automatickú synchronizáciu dát z TiDo API.
              </p>
            </div>
          </div>

          <div>
            <h3 className="font-display text-sm font-semibold mb-2">Otváracie hodiny & min. obsluha</h3>
            <div className="card-flat overflow-hidden">
              <table>
                <thead>
                  <tr>
                    <th>Deň</th>
                    <th>Otvorené</th>
                    <th>Otvorené od</th>
                    <th>Otvorené do</th>
                    <th>Min. ráno</th>
                    <th>Min. večer</th>
                  </tr>
                </thead>
                <tbody>
                  {WEEKDAYS_SK.map((wd, i) => {
                    const cfg = days[i] || DEFAULT_DAY();
                    return (
                      <tr key={i}>
                        <td className="font-medium">{wd}</td>
                        <td>
                          <input
                            type="checkbox"
                            data-testid={`day-${i}-open`}
                            checked={!cfg.closed}
                            onChange={(e) => updateDay(i, "closed", !e.target.checked)}
                          />
                        </td>
                        <td>
                          <input
                            type="time"
                            value={cfg.open_from || ""}
                            disabled={cfg.closed}
                            onChange={(e) => updateDay(i, "open_from", e.target.value)}
                            className="w-28"
                            data-testid={`day-${i}-from`}
                          />
                        </td>
                        <td>
                          <input
                            type="time"
                            value={cfg.open_to || ""}
                            disabled={cfg.closed}
                            onChange={(e) => updateDay(i, "open_to", e.target.value)}
                            className="w-28"
                            data-testid={`day-${i}-to`}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.5"
                            value={cfg.min_morning ?? 0}
                            disabled={cfg.closed}
                            onChange={(e) => updateDay(i, "min_morning", parseFloat(e.target.value) || 0)}
                            className="w-20 tabular"
                            data-testid={`day-${i}-morn`}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.5"
                            value={cfg.min_evening ?? 0}
                            disabled={cfg.closed}
                            onChange={(e) => updateDay(i, "min_evening", parseFloat(e.target.value) || 0)}
                            className="w-20 tabular"
                            data-testid={`day-${i}-eve`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-[var(--text-muted)] mt-2">
              Predvyplnené z údajov o stredisku 110041 Nová Lesná. Min. ráno = prvá hodina po otvorení, Min. večer = posledná hodina pred zatvorením.
            </p>
          </div>

          {isEdit && (
            <div className="pt-4 border-t border-[var(--border-default)]">
              <h3 className="font-display text-base font-semibold mb-2">Import dát z CSV</h3>
              <p className="text-xs text-[var(--text-secondary)] mb-3">
                Manuálny import z Excel/CSV pre historické dáta, ktoré nie sú v TiDo. Bežne používaj TiDo synchronizáciu (tlačidlo "Znovu stiahnuť z TiDo" v hornom paneli detailu strediska).
              </p>
              <DataImport storeId={store.id} />
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-[var(--border-default)]">
            <button type="button" onClick={onClose} className="btn-ghost" data-testid="cancel-form-button">
              Zrušiť
            </button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="submit-form-button">
              {saving ? "Ukladám..." : isEdit ? "Uložiť zmeny" : "Vytvoriť stredisko"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
